import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import classes from '../agenda.module.scss';
import { ReactComponent as TimeSvg } from '../../../../assets/icons/time.svg';
import { ReactComponent as AddressSvg } from '../../../../assets/icons/home.svg';
import { ReactComponent as RouteSvg } from '../../../../assets/icons/address_mark.svg';
import { ReactComponent as AddPostIcon } from '../../../../assets/icons/chatPlus.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit_btn_icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/close.svg';
import { ReactComponent as RepsIcon } from '../../../../assets/icons/team.svg';

import AccountHelpers from '../../../../utils/accountHelpers';
import Button from '../../../_common/Form/Buttons/Button';
import StatusField from '../../../_common/StatusField/StatusField';
import RouteModal from '../RouteModal';
import ImageModal from '../../../_common/Modal/ImageModal/ImageModal';

const VisitView = ({
  currentDate, visitedUsers, showPrevBtn, showNextBtn, changeVisitDate, openingNoteAddModal,
  setAgendaInfo, setDeletingId, appointment, setAppointment,
}) => {
  const currentUserId = AccountHelpers.getUser().id;
  const isAdmin = AccountHelpers.isInOneOfRoles(['Admin', 'Pharmacy']);

  const groupByRep = _.groupBy(_.orderBy(visitedUsers, 'id'), 'Rep');

  const timeout = useRef(null);

  const [mapInfo, setMapInfo] = useState(null);

  const [imageInFocus, setImageInFocus] = useState('');

  const onFocusImage = (imageUrl) => {
    timeout.current = setTimeout(() => {
      setImageInFocus(imageUrl);
    }, 1500);
  };

  const onBlurImage = () => {
    clearTimeout(timeout.current);
  };

  const renderAppointment = (appointments) => (
    <ul className={classes.info_wrapper}>
      <h5>
        {appointments[0].Rep}

        <div>
          <Button
            className={classes.map_button}
            reverseColor
            title={(
              <>
                <RouteSvg />

                Map
              </>
            )}
            onClick={() => setMapInfo({ userId: appointments[0].repId, appointments })}
          />

          <span className={classes.apps}>{`${appointments.length} appts`}</span>
        </div>
      </h5>

      {appointments.map((d) => {
        const address = _.compact([d.physician?.addressStreet,
          d.physician?.addressCity, d.physician?.addressState, d.physician?.addressZipCode]);

        return (
          <li key={d.id}>
            {(isAdmin || +d.RepId === +currentUserId) && (
              <div className={classes.settings}>
                <EditIcon
                  className={classes.edit}
                  onClick={() => setAgendaInfo(d)}
                />

                <DeleteIcon
                  className={classes.delete}
                  onClick={() => setDeletingId(d.id)}
                />
              </div>
            )}

            <TimeSvg className={classes.time_svg} />

            {moment.utc(d.dateTime).local().format('hh:mm a')}
            {' '}

            <NavLink to={`/physicians/${d.PhysicianId}`}>
              <span className={classes.physician_name}>
                {d.Physician || d.PhysicianName}
              </span>
            </NavLink>

            {' '}
            {d.group && (
              <span className={classes.group_name}>
                (
                {d.group}
                )
              </span>
            )}

            <div className={classes.actual_time}>
              <TimeSvg />
              Check in time
              {' '}

              {moment.utc(d.actualDateTime || d.dateTime).local().format('hh:mm a')}
            </div>

            {!!address.length && (
              <div className={classes.address}>
                <AddressSvg className={classes.address_svg} />

                {address.join(', ')}
              </div>
            )}

            <div className={classes.notes}>
              {d.visitNotes.map((v) => {
                const isImage = ['png', 'jpeg', 'jpg', 'gif'].includes(v.fileUrl?.split('.').at(-1));

                return (
                  v.fileUrl
                    ? (
                      <a
                        href={v.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={`${classes.note} ${classes.file}`}
                        onMouseOver={() => isImage && onFocusImage(v.fileUrl)}
                        onMouseOut={onBlurImage}
                      >
                        {v.note}
                      </a>
                    )

                    : (
                      <div key={v.id} className={classes.note}>
                        {v.note}
                      </div>
                    )
                );
              })}

              <div className={classes.check_status}>
                Check in Type:
                {' '}
                <b>{d.checkInStatus}</b>
              </div>

              <div className={classes.status_wrapper}>
                <StatusField status={d.status} className={classes.status_field} />

                <Button
                  className={classes.add_post_wrapper}
                  reverseColor
                  onClick={() => openingNoteAddModal({ visitId: d.id, physicianId: d.PhysicianId })}
                  title={(
                    <>
                      <AddPostIcon />

                      Add Note
                    </>
                  )}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={classes.visit_view_wrapper}>
      <div className={classes.date_wrapper}>
        {showPrevBtn && (
          <div
            className={classes.prev_arrow}
            onClick={() => changeVisitDate(true)}
            role="button"
            tabIndex="0"
          >
            <div />
          </div>
        )}

        <div className={classes.date}>
          {moment(currentDate).format('dddd, MMMM DD, YYYY')}
        </div>

        {showNextBtn && (
          <div
            className={classes.next_arrow}
            onClick={() => changeVisitDate()}
            role="button"
            tabIndex="0"
          >
            <div />
          </div>
        )}
      </div>

      <div className={classes.view_wrapper}>
        <div className={classes.view_content}>
          <h5>
            <RepsIcon />
            Sales reps
            <span>{`${Object.keys(groupByRep).length} total`}</span>
          </h5>

          <div className={classes.view_reps_wrapper}>
            {_.map(groupByRep, (data, key) => (
              <div
                key={key}
                className={key === appointment?.[0]?.Rep ? classes.active : ''}
                onClick={() => setAppointment(data)}
              >
                <p>
                  <span className={classes.name}>
                    {key}
                  </span>

                  <span className={classes.appts}>
                    {data.length}
                    {' '}
                    appts
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>

        {appointment && renderAppointment(appointment)}
      </div>

      <RouteModal
        mapInfo={mapInfo}
        date={currentDate}
        onClose={() => setMapInfo({})}
      />

      <ImageModal
        image={imageInFocus}
        onClose={() => setImageInFocus('')}
      />
    </div>
  );
};

VisitView.propTypes = {
  visitedUsers: PropTypes.array.isRequired,
  currentDate: PropTypes.string.isRequired,
  showPrevBtn: PropTypes.bool.isRequired,
  showNextBtn: PropTypes.bool.isRequired,
  changeVisitDate: PropTypes.func.isRequired,
  openingNoteAddModal: PropTypes.func.isRequired,
  setAgendaInfo: PropTypes.func.isRequired,
  setDeletingId: PropTypes.func.isRequired,
};

export default VisitView;
